import { decodeHtmlEntities } from "../../../../../../common/helpers/decodeHtmlEntities";

/**
 * Generic type for a raw taxonomy term that includes a potential translation.
 */
interface RawTerm {
  id: number;
  name: string;
  slug: string;
  link: string | boolean;
  translated_name?: string;
}

/**
 * A generic, pure helper function to transform an array of raw taxonomy terms
 * from the 'enriched' extensions (like categories or tags) into a clean, translated format.
 *
 * @param rawTerms The array of raw term objects from the API.
 * @param shouldTranslate A boolean indicating if translations should be applied.
 * @returns A new array of transformed term objects.
 */
export const transformEnrichedTaxonomies = <T extends RawTerm>(
  rawTerms: T[] | undefined,
  shouldTranslate: boolean
): T[] => {
  if (!Array.isArray(rawTerms)) {
    return [];
  }

  return rawTerms.map((term) => {
    // Determine the final name string
    let finalName = term.name;

    // If we should translate and a translation exists, use it
    if (shouldTranslate && term.translated_name) {
      finalName = term.translated_name;
    }

    // Return a new object to avoid mutating the original cached data.
    // Decode HTML entities in the name.
    return {
      ...term,
      name: decodeHtmlEntities(finalName),
    };
  });
};
